import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './VerifyMembership.css';

const VerifyMembership = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [membership, setMembership] = useState(null);
  const [error, setError] = useState('');

  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    // Check if already authenticated
    const token = localStorage.getItem('adminToken');
    if (token) {
      setAuthenticated(true);
      fetchMembershipDetails(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/api/v1/auth/login`,
        credentials
      );

      if (response.data.token) {
        const token = response.data.token;
        localStorage.setItem('adminToken', token);
        setAuthenticated(true);
        fetchMembershipDetails(token);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembershipDetails = async (token) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000'}/api/v1/memberships/verify/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setMembership(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load membership details');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        setAuthenticated(false);
      } else {
        setError(err.response?.data?.message || 'Failed to load membership details');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    setAuthenticated(false);
    setMembership(null);
    navigate('/');
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!authenticated) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="verify-card">
            <div className="verify-header">
              <h1>
                <i className="bi bi-shield-lock"></i> Membership Verification
              </h1>
              <p>Admin authentication required</p>
            </div>
            <form onSubmit={handleLogin} className="login-form">
              {error && (
                <div className="alert alert-danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {error}
                </div>
              )}
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  className="form-control"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  required
                  disabled={loading}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Logging in...
                  </>
                ) : (
                  <>
                    <i className="bi bi-box-arrow-in-right me-2"></i>
                    Login
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (loading && !membership) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading membership details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !membership) {
    return (
      <div className="verify-membership-page">
        <div className="verify-container">
          <div className="verify-card">
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
            <button className="btn btn-secondary" onClick={handleLogout}>
              <i className="bi bi-arrow-left me-2"></i>
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="verify-membership-page">
      <div className="verify-container">
        <div className="verify-card">
          <div className="verify-header">
            <h1>
              <i className="bi bi-award"></i> Membership Details
            </h1>
            <button className="btn btn-sm btn-outline-light" onClick={handleLogout}>
              <i className="bi bi-box-arrow-right me-2"></i>
              Logout
            </button>
          </div>

          {membership && (
            <div className="membership-details">
              <div className="status-section">
                <div className={`status-badge ${membership.isActive ? 'status-active' : 'status-inactive'}`}>
                  {membership.isActive ? (
                    <>
                      <i className="bi bi-check-circle-fill me-2"></i>
                      Active Membership
                    </>
                  ) : membership.isExpired ? (
                    <>
                      <i className="bi bi-x-circle-fill me-2"></i>
                      Expired
                    </>
                  ) : (
                    <>
                      <i className="bi bi-clock-fill me-2"></i>
                      {membership.status}
                    </>
                  )}
                </div>
              </div>

              <div className="details-grid">
                <div className="detail-item">
                  <div className="detail-label">Membership Code</div>
                  <div className="detail-value code-value">{membership.code || 'N/A'}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Type</div>
                  <div className="detail-value">{membership.type}</div>
                </div>

                <div className="detail-item full-width">
                  <div className="detail-label">Name</div>
                  <div className="detail-value">{membership.name}</div>
                </div>

                {membership.coupleName && (
                  <div className="detail-item full-width">
                    <div className="detail-label">Partner Name</div>
                    <div className="detail-value">{membership.coupleName}</div>
                  </div>
                )}

                <div className="detail-item">
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{membership.email}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Phone</div>
                  <div className="detail-value">{membership.phone}</div>
                </div>

                {membership.couplePhone && (
                  <div className="detail-item">
                    <div className="detail-label">Partner Phone</div>
                    <div className="detail-value">{membership.couplePhone}</div>
                  </div>
                )}

                {membership.residenceCountry && (
                  <div className="detail-item">
                    <div className="detail-label">Country</div>
                    <div className="detail-value">{membership.residenceCountry}</div>
                  </div>
                )}

                <div className="detail-item">
                  <div className="detail-label">Start Date</div>
                  <div className="detail-value">{formatDate(membership.startDate)}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">End Date</div>
                  <div className="detail-value">{formatDate(membership.endDate)}</div>
                </div>

                <div className="detail-item">
                  <div className="detail-label">Member Since</div>
                  <div className="detail-value">{formatDate(membership.createdAt)}</div>
                </div>
              </div>

              {membership.photo && (
                <div className="photo-section">
                  <div className="detail-label">Photo</div>
                  <img src={membership.photo} alt="Member" className="member-photo" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyMembership;
